// Dependencies
import { Component } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import { VIEW_MODE } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { portRecord } from 'services/ports';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    SetSystemLoadingAction,
    setSystemLoadingAction,
} from 'store/actions/errorsActions';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import { setUserAction } from 'store/actions/userActions';
import { OpenModal, PortType, RootState, UserPortType, UserType } from 'types';
import { MapControlsInterface } from 'types/functions';
import { PortManager } from 'utils/IndexedDB';
import { hasPermission } from 'utils/permission';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';
import { getId } from 'utils/formHandlers';

interface SystemComponentProps {
    mapControls: MapControlsInterface;
    user: UserType;
    port: PortType;
    active: boolean;
    setLayoutAction: SetLayoutAction;
    openModelFunction: OpenModal;
    setSystemLoadingAction: SetSystemLoadingAction;
    setErrorHandlerAction: SetErrorHandlerAction;
}

interface SystemComponentState {
    active: boolean;
    changePortMenu: boolean;
    adminMenu: boolean;
    ports: UserPortType[];
}

class System extends Component<SystemComponentProps, SystemComponentState> {
    constructor(props: SystemComponentProps) {
        super(props);
        this.state = {
            active: false,
            changePortMenu: this.props.user.user_ports.length >= 2,
            adminMenu: this.props.user.user_ports.length < 2,
            ports: this.props.user.user_ports,
        };
    }

    static getDerivedStateFromProps(props: SystemComponentProps, state: SystemComponentState) {
        if (props.active !== state.active) {
            return { active: props.active };
        }
        return null;
    }

    componentDidMount(): void {
        //@ts-ignore
        window.toggleVideo = this.helpVideos;
    }

    toggleChangePortMenu = () => {
        this.setState({ changePortMenu: !this.state.changePortMenu, adminMenu: false });
    };

    toggleAdminMenu = () => {
        this.setState({ adminMenu: !this.state.adminMenu, changePortMenu: false });
    };

    helpVideos = () => {
        this.props.openModelFunction(
            'helpVideosModel',
            20,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('helpVideosModel');
                },
            },
            true
        );
    };

    upload = () => {
        this.props.openModelFunction(
            'uploadUpdateModel',
            20,
            'small',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('uploadUpdateModel');
                },
            },
            true
        );
    };

    keyboardShortcuts = () => {
        this.props.openModelFunction(
            'keyboardShortcuts',
            22,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('keyboardShortcuts');
                },
            },
            true
        );
    };

    about = () => {
        this.props.openModelFunction(
            'aboutModel',
            21,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('aboutModel');
                },
            },
            true
        );
    };

    settings = () => {
        this.props.setLayoutAction([
            {
                action: 'UPDATE_MENU',
                data: {
                    name: 'system',
                    section: 'system',
                    state: false,
                },
            },
        ]);

        this.props.openModelFunction(
            'settingModel',
            21,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('settingModel');
                },
            },
            true
        );
    };

    systemConsole = () => {
        this.props.setLayoutAction([
            {
                action: 'UPDATE_MENU',
                data: {
                    name: 'systemConsole',
                    section: 'system',
                    state: true,
                },
            },
        ]);
    };

    changePort = async (port_id: number): Promise<void> => {
        this.props.setSystemLoadingAction(true);

        try {
            const res = await portRecord(port_id);
            const originalPort: PortType = res.items;

            // Update backgrounds
            originalPort.port_backgrounds = originalPort.port_backgrounds.map((x) => ({
                ...x,
                background: {
                    ...x.background,
                    status: x.background.name === 'ENC' ? 'active' : 'inactive',
                },
            }));

            originalPort.meta_data.pilotagePlans.plans = originalPort.meta_data.pilotagePlans.plans.map((x) => {
                if (!x.key) {
                    x.key = getId();
                }
                return x;
            });

            // Save **everything in IndexedDB** using PortManager
            const portManager = new PortManager();
            await portManager.savePort(originalPort);

            // Save only port_id in localStorage if you still need quick access
            localStorage.setItem('port_id', originalPort.id.toString());

            // this.props.setSystemLoadingAction(false);

            // Navigation is still handled here
            //@ts-ignore
            window.location = `/${VIEW_MODE === 'live' ? 'time' : VIEW_MODE}`;
        } catch (error) {
            this.props.setSystemLoadingAction(false);
            this.props.setErrorHandlerAction(error);
        }
    };

    saveChunkedBySize = (baseKey: string, data: any[], maxKB: number) => {
        const maxBytes = maxKB * 1024;

        // 🔹 1. Delete old chunks
        Object.keys(localStorage)
            .filter((key) => key.startsWith(`${baseKey}_`))
            .forEach((key) => localStorage.removeItem(key));

        localStorage.removeItem(`${baseKey}_chunks`);

        let chunk: any[] = [];
        let chunkIndex = 0;

        for (const item of data) {
            chunk.push(item);

            const chunkString = JSON.stringify(chunk);
            const chunkSize = new Blob([chunkString]).size;

            // If exceeded size → remove last item and save previous chunk
            if (chunkSize > maxBytes) {
                chunk.pop(); // remove overflow item
                console.log(chunkSize, chunkString);
                localStorage.setItem(`${baseKey}_${chunkIndex}`, JSON.stringify(chunk));

                chunkIndex++;

                // start new chunk with overflow item
                chunk = [item];
            }
        }

        // Save remaining chunk
        if (chunk.length > 0) {
            localStorage.setItem(`${baseKey}_${chunkIndex}`, JSON.stringify(chunk));
            chunkIndex++;
        }

        // Save metadata
        localStorage.setItem(`${baseKey}_chunks`, chunkIndex.toString());
    };

    signOut = (): void => {
        const UUID = localStorage.getItem('uuid');

        document.cookie = 'cognito_id_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

        localStorage.removeItem('token');
        localStorage.removeItem('port');
        localStorage.removeItem('user');
        localStorage.removeItem('uuid');

        // Redirect after cookie deletion
        setTimeout(() => {
            window.location.href = `/logout/${UUID}`;
        }, 500);
    };

    render() {
        return (
            <>
                <div
                    className={twclsx(
                        this.state.active ? 'block' : 'hidden',
                        'z-40',
                        'fixed',
                        'right-0',
                        'top-0',
                        'h-screen',
                        'w-screen',
                        'select-none',
                        'bg-black',
                        'bg-opacity-30'
                    )}
                ></div>
                <div
                    className={twclsx(
                        this.state.active ? 'right-0' : 'right-out',
                        'flex',
                        'flex-col',
                        'z-40',
                        'position-transition',
                        'fixed',
                        'w-menu',
                        'h-screen',
                        'top-0',
                        'bg-white',
                        'shadow-xl'
                    )}
                >
                    <div className="flex justify-between p-4">
                        <div className="overflow-hidden">
                            <Image alt="" className="w-48  h-16 -mt-10" src="/static/images/logo.svg"></Image>
                        </div>
                        <div
                            onClick={() => {
                                this.props.setLayoutAction([
                                    {
                                        action: 'UPDATE_MENU',
                                        data: {
                                            name: 'system',
                                            section: 'system',
                                            state: false,
                                        },
                                    },
                                ]);
                            }}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    {this.state.ports.length >= 2 && (
                        <div className="pl-4 pr-4 w-full">
                            <div className="flex justify-between mb-2 border-b pb-2 border-gray-400">
                                <div className="flex justify-between">
                                    <Icon className="w-5 h-5">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M3 5a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10z" />
                                        <path d="M7 20h10" />
                                        <path d="M9 16v4" />
                                        <path d="M15 16v4" />
                                    </Icon>
                                    <div className="font-semibold text-lg ml-2">{translate('Change port')}</div>
                                </div>

                                <div onClick={this.toggleChangePortMenu} className="cursor-pointer items-end">
                                    {this.state.changePortMenu ? (
                                        <Icon>
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M6 15l6 -6l6 6" />
                                        </Icon>
                                    ) : (
                                        <Icon>
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M6 9l6 6l6 -6" />
                                        </Icon>
                                    )}
                                </div>
                            </div>
                            {this.state.changePortMenu && (
                                <Scrollbars autoHeight style={{ maxHeight: 200 }}>
                                    {this.state.ports
                                        .sort((a: UserPortType, b: UserPortType) =>
                                            a.port.name.localeCompare(b.port.name)
                                        )
                                        .map((x, i) => {
                                            return (
                                                <div
                                                    onClick={() => {
                                                        this.changePort(x.port.id);
                                                    }}
                                                    key={i}
                                                    className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-activeTextColor focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                                >
                                                    <div className="font-bold"> {x.port.name}</div>
                                                    <div className="text-sm"> {x.port.client_name}</div>
                                                </div>
                                            );
                                        })}
                                </Scrollbars>
                            )}
                        </div>
                    )}

                    {hasPermission(this.props.user, this.props.port.id, [
                        'tagsAdmin',
                        'commentsAdmin',
                        'usersAdmin',
                        'searchsAdmin',
                    ]) && (
                        <div className="pl-4 pr-4 w-full">
                            <div className="flex justify-between mb-2 border-b pb-2 border-gray-400">
                                <div className="flex justify-between">
                                    <Icon className="w-5 h-5">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                        <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                                        <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                        <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                                        <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                        <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
                                    </Icon>
                                    <div className="font-semibold text-lg ml-2">{translate('System admin')}</div>
                                </div>

                                <div onClick={this.toggleAdminMenu} className="cursor-pointer items-end">
                                    {this.state.adminMenu ? (
                                        <Icon>
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M6 15l6 -6l6 6" />
                                        </Icon>
                                    ) : (
                                        <Icon>
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M6 9l6 6l6 -6" />
                                        </Icon>
                                    )}
                                </div>
                            </div>
                            {this.state.adminMenu && (
                                <>
                                    {hasPermission(this.props.user, this.props.port.id, ['commentsAdmin']) && (
                                        <div
                                            onClick={() => {
                                                this.props.mapControls.chart?.hideTooltips();

                                                this.props.setLayoutAction([
                                                    {
                                                        action: 'UPDATE_MENU',
                                                        data: {
                                                            name: 'system',
                                                            section: 'system',
                                                            state: false,
                                                        },
                                                    },

                                                    {
                                                        action: 'UPDATE_SECTION',
                                                        data: {
                                                            name: 'commentsManager',
                                                            state: true,
                                                            display: true,
                                                            disable: false,
                                                        },
                                                    },
                                                ]);
                                            }}
                                            className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-500 focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                        >
                                            <div className="font-bold">{translate('Comments Admin')}</div>
                                            <div className="text-sm">
                                                {translate('Manages comments for collaboration and documentation')}.
                                            </div>
                                        </div>
                                    )}
                                    {hasPermission(this.props.user, this.props.port.id, ['tagsAdmin']) && (
                                        <div
                                            onClick={() => {
                                                this.props.mapControls.chart?.hideTooltips();

                                                this.props.setLayoutAction([
                                                    {
                                                        action: 'UPDATE_MENU',
                                                        data: {
                                                            name: 'system',
                                                            section: 'system',
                                                            state: false,
                                                        },
                                                    },
                                                    // ...MANAGERS.map((x) => {
                                                    //     return {
                                                    //         action: 'CLOSE_SECTION',
                                                    //         data: {
                                                    //             name: x,
                                                    //             state: false,
                                                    //             display: false,
                                                    //         },
                                                    //     };
                                                    // }),
                                                    {
                                                        action: 'UPDATE_SECTION',
                                                        data: {
                                                            name: 'tagsManager',
                                                            state: true,
                                                            display: true,
                                                            disable: false,
                                                        },
                                                    },
                                                ]);
                                            }}
                                            className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-500 focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                        >
                                            <div className="font-bold">{translate('Tags Admin')}</div>
                                            <div className="text-sm">
                                                {translate('Organizes metadata tags for efficient categorization')}.
                                            </div>
                                        </div>
                                    )}
                                    {hasPermission(this.props.user, this.props.port.id, ['isAdmin']) && (
                                        <div
                                            onClick={() => {
                                                this.props.mapControls.chart?.hideTooltips();

                                                this.props.setLayoutAction([
                                                    {
                                                        action: 'UPDATE_MENU',
                                                        data: {
                                                            name: 'system',
                                                            section: 'system',
                                                            state: false,
                                                        },
                                                    },

                                                    {
                                                        action: 'UPDATE_SECTION',
                                                        data: {
                                                            name: 'portsManager',
                                                            state: true,
                                                            display: true,
                                                            disable: false,
                                                        },
                                                    },
                                                ]);
                                            }}
                                            className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-500 focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                        >
                                            <div className="font-bold">{translate('Ports Admin')}</div>
                                            <div className="text-sm">{translate('Organizes ports')}.</div>
                                        </div>
                                    )}
                                    {hasPermission(this.props.user, this.props.port.id, ['usersAdmin']) && (
                                        <div
                                            onClick={() => {
                                                this.props.mapControls.chart?.hideTooltips();

                                                this.props.setLayoutAction([
                                                    {
                                                        action: 'UPDATE_MENU',
                                                        data: {
                                                            name: 'system',
                                                            section: 'system',
                                                            state: false,
                                                        },
                                                    },

                                                    {
                                                        action: 'UPDATE_SECTION',
                                                        data: {
                                                            name: 'usersManager',
                                                            state: true,
                                                            display: true,
                                                            disable: false,
                                                        },
                                                    },
                                                ]);
                                            }}
                                            className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-500 focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                        >
                                            <div className="font-bold">{translate('User Admin')}</div>
                                            <div className="text-sm">
                                                {translate('Controls user access and authentication securely')}.
                                            </div>
                                        </div>
                                    )}
                                    {hasPermission(this.props.user, this.props.port.id, ['searchsAdmin']) && (
                                        <div
                                            onClick={() => {
                                                this.props.mapControls.chart?.hideTooltips();
                                                this.props.setLayoutAction([
                                                    {
                                                        action: 'UPDATE_MENU',
                                                        data: {
                                                            name: 'system',
                                                            section: 'system',
                                                            state: false,
                                                        },
                                                    },

                                                    {
                                                        action: 'UPDATE_SECTION',
                                                        data: {
                                                            name: 'searchesManager',
                                                            state: true,
                                                            display: true,
                                                            disable: false,
                                                        },
                                                    },
                                                ]);
                                            }}
                                            className="  flex flex-col py-1 cursor-pointer leading-5 text-gray-700  hover:text-primary-500 focus:outline-none focus:bg-gray-100 focus:text-primary-500"
                                        >
                                            <div className="font-bold">{translate('Search Admin')}</div>
                                            <div className="text-sm">
                                                {translate('Governs effective search functionality and configuration')}.
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="pl-4 pr-4 w-full hidden">
                        <div onClick={this.upload} className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
                                <path d="M9 15l3 -3l3 3"></path>
                                <path d="M12 12l0 9"></path>
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('Update UI')}</div>
                        </div>
                    </div>
                    <div className="pl-4 pr-4 w-full">
                        <div onClick={this.helpVideos} className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M2 8a4 4 0 0 1 4 -4h12a4 4 0 0 1 4 4v8a4 4 0 0 1 -4 4h-12a4 4 0 0 1 -4 -4v-8z" />
                                <path d="M10 9l5 3l-5 3z" />
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('TransitAnalyst Videos')}</div>
                        </div>
                    </div>
                    <div className="pl-4 pr-4 w-full">
                        <div onClick={this.keyboardShortcuts} className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path d="M2 6m0 2a2 2 0 0 1 2 -2h16a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-16a2 2 0 0 1 -2 -2z"></path>
                                <path d="M6 10l0 .01"></path>
                                <path d="M10 10l0 .01"></path>
                                <path d="M14 10l0 .01"></path>
                                <path d="M18 10l0 .01"></path>
                                <path d="M6 14l0 .01"></path>
                                <path d="M18 14l0 .01"></path>
                                <path d="M10 14l4 .01"></path>
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('keyboard shortcuts')}</div>
                        </div>
                    </div>
                    <div className="pl-4 pr-4 w-full">
                        <div onClick={this.about} className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M12 9h.01" />
                                <path d="M11 12h1v4h1" />
                                <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z" />
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('About')}</div>
                        </div>
                    </div>
                    {hasPermission(this.props.user, this.props.port.id, ['isAdmin']) && (
                        <div className="pl-4 pr-4 w-full">
                            <div onClick={this.systemConsole} className="flex my-2 cursor-pointer">
                                <Icon className="w-5 h-5">
                                    <path d="M8 9l3 3l-3 3"></path>
                                    <path d="M13 15l3 0"></path>
                                    <path d="M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z"></path>
                                </Icon>
                                <div className="font-semibold text-lg ml-2">{translate('system console')}</div>
                            </div>
                        </div>
                    )}

                    <div onClick={this.settings} className="pl-4 pr-4 w-full">
                        <div className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                                <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('Settings')}</div>
                        </div>
                    </div>

                    <div className="pl-4 pr-4 w-full">
                        <div onClick={this.signOut} className="flex my-2 cursor-pointer">
                            <Icon className="w-5 h-5">
                                <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2" />
                                <path d="M9 12h12l-3 -3" />
                                <path d="M18 15l3 -3" />
                            </Icon>
                            <div className="font-semibold text-lg ml-2">{translate('log out')}</div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const systemstateToProps = (state: RootState) => ({
    user: state.user.user,
    port: state.port.port,
    openModelFunction: state.layout.openModelFunction,
    mapControls: state.map.mapControls,
});
const systemDispatchToProps = (dispatch: Dispatch) => ({
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),

    setUserIdAction: bindActionCreators(setUserAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(systemstateToProps, systemDispatchToProps)(System);
